import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { LeaseForm, LeaseFormValues } from "../../components/lease-form";
import { Modal } from "../../components/modal";
import {
  formValuesToResidencyName,
  residencyNameToFormValues,
} from "../../components/residency-name-input";
import { useAdditionalFields } from "../../hooks/addition-fields";
import { useGetLease } from "../../queries/lease";
import {
  parseAdditionalFieldToFormValue,
  parseFormValuesToAdditionalFields,
} from "../../services/additional-fields";
import { updateLease } from "../../services/lease";
import { formatDate } from "../../utils/date";
import { navigateBack } from "../../utils/navigate-back";

export function LeaseEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  if (!id) {
    throw new Error("Lease ID is required");
  }

  const { data: lease, isLoading: isLoadingLease } = useGetLease(id);

  const { data: additionalFields } = useAdditionalFields("lease");
  const queryClient = useQueryClient();

  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const [formValues, setFormValues] = useState<LeaseFormValues>({
    apartmentId: "",
    fromDate: "",
    toDate: "",
    useCustomName: false,
    customName: "",
    tenants: [],
    additionalFields: additionalFields.reduce(
      (acc, field) => ({
        ...acc,
        [field.name]: "",
      }),
      {},
    ),
  });

  const isLoading = isLoadingLease || loading;

  useEffect(() => {
    if (lease) {
      setFormValues({
        apartmentId: lease.apartment.id,
        fromDate: formatDate(lease.fromDate),
        toDate: lease.toDate ? formatDate(lease.toDate) : "",
        ...residencyNameToFormValues(lease.customName),
        tenants:
          lease?.tenants.map((tenant) => {
            if ("person" in tenant) {
              return {
                personId: tenant.person.id,
              };
            } else if ("organization" in tenant) {
              return {
                organizationId: tenant.organization.id,
              };
            } else {
              throw new Error("Invalid tenant");
            }
          }) || [],
        additionalFields: additionalFields.reduce(
          (acc, field) => ({
            ...acc,
            [field.name]: parseAdditionalFieldToFormValue(
              lease?.additionalData?.[field.name],
            ),
          }),
          {},
        ),
      });
    }
  }, [lease, additionalFields]);

  const closeModal = () => {
    navigateBack(navigate, "/admin/register/lease");
  };

  return (
    <Modal open onClose={() => closeModal()}>
      <LeaseForm
        hasError={hasError}
        loading={isLoading}
        values={formValues}
        onCancel={() => closeModal()}
        onChange={setFormValues}
        onSubmit={() => {
          (async () => {
            setLoading(true);
            try {
              await updateLease(id, {
                apartmentId: formValues.apartmentId,
                fromDate: new Date(formValues.fromDate),
                toDate: formValues.toDate
                  ? new Date(formValues.toDate)
                  : undefined,
                customName: formValuesToResidencyName(formValues),
                tenants: formValues.tenants,
                ...(formValues.additionalFields && {
                  additionalData: parseFormValuesToAdditionalFields(
                    formValues.additionalFields,
                    additionalFields,
                    true,
                  ),
                }),
              });
              Promise.all([
                queryClient.invalidateQueries({
                  queryKey: ["lease", id],
                }),
                queryClient.invalidateQueries({
                  queryKey: ["leases"],
                }),
              ]);
              closeModal();
            } catch (error: unknown) {
              setHasError(true);
            } finally {
              setLoading(false);
            }
          })();
        }}
      />
    </Modal>
  );
}
